import {
  Badge,
  Box,
  Checkbox,
  Flex,
  HStack,
  Pressable,
  Progress,
  Radio,
  Spinner,
  Switch,
  Text,
  VStack,
} from 'native-base'
import React, { type ReactElement, useMemo } from 'react'

import ActionCardChevronSVG from '../assets/ActionCardChevronSVG'
import { colors } from '../utils/theme/configureTheme'

// eslint-disable-next-line react-refresh/only-export-components
export enum CARD_ACTION_TYPES {
  navigate = 'navigate',
  radioSelect = 'radioSelect',
  checkBoxSelect = 'checkBoxSelect',
  toggle = 'toggle',
  customCardAction = 'customCardAction',
}

interface IconProps {
  width?: number
  height?: number
  borderRadius?: string | number
  borderWidth?: number
  borderColor?: string
  backgroundColor?: string
}

interface ActionCardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  CardIcon: ReactElement<any> | null
  iconProps?: IconProps
  iconBadgeCount?: number
  badgeText?: string
  titleText?: string
  descriptionText?: string
  assistiveText?: string
  cardAction?: CARD_ACTION_TYPES | string
  onPress?: () => void
  isDisabled?: boolean
  cardActionStyleProps?: object
  cardActionCallback?: () => void
  shouldShowProgressBar?: boolean
  progressBarValue?: number
  toggleValue?: boolean
  checkboxValue?: boolean
  actionName?: string
  actionLoading?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  radioValue?: any
  badgeColor?: string
  cardStyleProps?: object
  titleTextStyles?: object
  titleTextContainerStyles?: object
  descriptionTextStyles?: object
  customCardActionReactChild?: ReactElement
  isSelected?: boolean
  selectedBackgroundColor?: string
  selectedBorderColor?: string
  borderColor?: string
}

const ActionCard: React.FunctionComponent<ActionCardProps> = ({
  CardIcon,
  iconProps,
  iconBadgeCount,
  badgeText,
  titleText,
  descriptionText,
  assistiveText,
  cardAction,
  onPress,
  isDisabled,
  cardActionStyleProps,
  cardActionCallback,
  shouldShowProgressBar,
  progressBarValue,
  toggleValue,
  checkboxValue,
  actionName,
  actionLoading,
  radioValue,
  badgeColor,
  cardStyleProps,
  titleTextStyles,
  titleTextContainerStyles,
  descriptionTextStyles,
  customCardActionReactChild,
  isSelected,
  selectedBackgroundColor,
  selectedBorderColor,
  borderColor,
}) => {
  const handleCardActionCallback = () => {
    if (!isDisabled && cardActionCallback) {
      cardActionCallback()
    }
  }

  const renderCardAction = () => {
    if (actionLoading) {
      return (
        <Box justifyContent="center" alignItems={'flex-end'} flex={1}>
          <Spinner size="sm" color={colors.brand.softBlack} />
        </Box>
      )
    }

    if (!cardAction) {
      return null
    }

    switch (cardAction) {
      case CARD_ACTION_TYPES.navigate:
        return (
          <Box justifyContent="center" alignItems={'flex-end'} flex={1}>
            <ActionCardChevronSVG styleProps={cardActionStyleProps} />
          </Box>
        )
      case CARD_ACTION_TYPES.toggle:
        return (
          <Box justifyContent="center" alignItems={'flex-end'} flex={1}>
            <Switch
              style={cardActionStyleProps}
              size={'sm'}
              onToggle={handleCardActionCallback}
              value={toggleValue}
              onTrackColor={colors.brand.selectedBlue}
              offTrackColor={colors.brand.softBlack}
              name={actionName}
              isDisabled={isDisabled}
            />
          </Box>
        )
      case CARD_ACTION_TYPES.radioSelect:
        return (
          <Box justifyContent="center" alignItems={'flex-end'} flex={1}>
            <Radio
              value={radioValue}
              key={radioValue}
              testID={`radio-option-${radioValue}`}
              colorScheme={'softBlack'}
              _checked={{ bg: colors.brand.selectedBlue }}
              accessibilityLabel={`radio-option-${radioValue}`}
              isDisabled={isDisabled}
            />
          </Box>
        )
      case CARD_ACTION_TYPES.checkBoxSelect:
        return (
          <Box justifyContent="center" alignItems={'flex-end'} flex={1}>
            <Checkbox
              borderColor={colors.brand.softBlack}
              borderWidth={1}
              backgroundColor={colors.brand.white}
              _checked={{
                backgroundColor: colors.brand.selectedBlue,
                borderColor: colors.brand.softBlack,
                borderWidth: 1,
                outlineColor: colors.brand.softBlack,
                _hover: {
                  borderColor: colors.brand.softBlack,
                },
              }}
              _hover={{
                outlineColor: colors.brand.softBlack,
              }}
              _icon={{
                color: colors.brand.softBlack,
              }}
              value={actionName ?? ''}
              isChecked={checkboxValue}
              onChange={handleCardActionCallback}
              accessibilityLabel={`checkbox-option-${actionName ?? 'unknown'}`}
              isDisabled={isDisabled}
              {...cardActionStyleProps}
            />
          </Box>
        )
      case CARD_ACTION_TYPES.customCardAction:
        return (
          <Box justifyContent="center" alignItems={'flex-end'} flex={1}>
            {isDisabled && customCardActionReactChild
              ? React.cloneElement(customCardActionReactChild, {
                  onPress: undefined,
                  onClick: undefined,
                  disabled: true,
                  isDisabled: true,
                } as Partial<unknown>)
              : customCardActionReactChild}
          </Box>
        )
      default:
        return null
    }
  }

  const handlePress = () => {
    if (!isDisabled && onPress) {
      onPress()
    }
  }

  const cardBackgroundColor = useMemo(() => {
    if (isDisabled) {
      return colors.brand.lightGrey
    }
    return isSelected && selectedBackgroundColor ? selectedBackgroundColor : colors.brand.white
  }, [isDisabled, isSelected, selectedBackgroundColor])

  const cardBorderColor = useMemo(() => {
    if (isSelected && selectedBorderColor) {
      return selectedBorderColor
    }
    if (borderColor) {
      return borderColor
    }
    return colors.brand.lightGrey
  }, [isSelected, selectedBorderColor, borderColor])

  return (
    <Pressable onPress={handlePress} disabled={isDisabled}>
      <Box
        borderRadius={6}
        borderWidth={1}
        px={4}
        py={3}
        width="100%"
        {...{ ...cardStyleProps, borderColor: cardBorderColor }}
        bg={cardBackgroundColor}
      >
        <HStack alignItems="center">
          {CardIcon && (
            <Box justifyContent="center">
              <Box
                width={iconProps?.width ?? 10}
                height={iconProps?.height ?? 10}
                borderRadius={iconProps?.borderRadius ?? 'full'}
                borderWidth={iconProps?.borderWidth ?? 1}
                borderColor={iconProps?.borderColor ?? colors.brand.lightGrey}
                backgroundColor={iconProps?.backgroundColor ?? colors.brand.lightGrey}
                justifyContent="center"
                alignItems="center"
                mr={2}
              >
                {CardIcon}
                {iconBadgeCount && <IconBadge count={iconBadgeCount} />}
              </Box>
            </Box>
          )}
          <VStack flexGrow={1}>
            {badgeText && (
              <HStack>
                <Badge
                  size="xs"
                  colorScheme={badgeColor}
                  variant="subtle"
                  mb={2}
                  ml={2}
                  borderRadius={4}
                  _text={{
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    color: isDisabled ? colors.brand.darkGrey : colors.brand.softBlack,
                  }}
                >
                  {badgeText}
                </Badge>
              </HStack>
            )}
            <VStack pr={5}>
              <VStack flexGrow={1} pl={2} width={'90%'}>
                {titleText?.length && (
                  <HStack
                    justifyContent="space-between"
                    alignItems="flex-start"
                    display="flex"
                    flexDirection="row"
                    mb={1}
                    {...titleTextContainerStyles}
                  >
                    <Text
                      fontWeight={500}
                      mb={0}
                      flexShrink={1}
                      fontSize={16}
                      lineHeight={18}
                      {...titleTextStyles}
                      color={
                        isDisabled
                          ? colors.brand.darkGrey
                          : titleTextStyles && 'color' in titleTextStyles && typeof titleTextStyles.color === 'string'
                            ? titleTextStyles.color
                            : colors.brand.softBlack
                      }
                    >
                      {titleText}
                    </Text>
                  </HStack>
                )}
                {descriptionText?.length && (
                  <Text
                    fontSize={14}
                    lineHeight={'16px'}
                    {...{
                      ...descriptionTextStyles,
                      color: isDisabled ? colors.brand.darkGrey : colors.brand.softBlack,
                    }}
                  >
                    {descriptionText}
                  </Text>
                )}
                {assistiveText?.length && <Text fontSize={12}>{assistiveText}</Text>}
              </VStack>
            </VStack>
          </VStack>
          {renderCardAction()}
        </HStack>
        {shouldShowProgressBar && (
          <Progress value={progressBarValue} mt={3} size={'sm'} _filledTrack={{ bg: colors.brand.vermillion }} />
        )}
      </Box>
    </Pressable>
  )
}

const IconBadge = ({ count }: { count: number }) => {
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      position="absolute"
      top={-7}
      right={-6}
      bg={colors.brand.lightGrey}
      borderColor={colors.brand.white}
      borderRadius={8}
      borderWidth={1}
      height={4}
      width={4}
      zIndex={10}
    >
      <Text fontSize={10} fontWeight={600}>
        {count}
      </Text>
    </Flex>
  )
}

export default ActionCard
